import React, { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import { MenuItem, Select } from '@mui/material';
import { getTodayFilter } from 'utils/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { updateTableState } from 'redux/slices/common/appSlice';

export type DateFilterValue = 'today' | 'yesterday' | 'thisMonth' | 'lastMonth' | 'all';

export const DateFilter = ({
    setFilter,
    filter,
    disabled,
    defaultValue = 'today',
}: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing: filter shape varies per consumer (setState or setter fn)
    setFilter?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing: filter shape varies per consumer
    filter?: any;
    disabled?: boolean;
    /** Initially selected option — must match the consumer's own initial filter (e.g. 'all' = no range). */
    defaultValue?: DateFilterValue;
}) => {
    // pageNumber is dropped on purpose: every filter change below resets it to 0.
    const { pageNumber: _pageNumber, ...tableState } = useSelector((state: RootState) => state.app.table);
    const [date, setDate] = useState<string>(defaultValue);
    const dispatch = useDispatch();
    const handleChange = useCallback(
        (value: string) => {
            if (!setFilter) return;
            const resetTablePagination = { pageNumber: 0, offset: 0 };
            dispatch(updateTableState({ ...tableState, ...resetTablePagination }));
            switch (value) {
                case 'today': {
                    setFilter({
                        ...filter,
                        ...getTodayFilter(),
                        ...resetTablePagination,
                    });
                    break;
                }
                case 'yesterday': {
                    const startDate = dayjs().add(-1, 'day').startOf('day');
                    const endDate = dayjs().subtract(1, 'day').endOf('day');
                    setFilter({
                        ...filter,
                        dayFrom: startDate.format(),
                        dayTo: endDate.format(),
                        value: 'yesterday',
                        ...resetTablePagination,
                    });
                    break;
                }
                case 'thisMonth': {
                    const startDate = dayjs().startOf('month');
                    const endDate = dayjs();
                    setFilter({
                        ...filter,
                        dayFrom: startDate.format(),
                        dayTo: endDate.format(),
                        value: 'thisMonth',
                        ...resetTablePagination,
                    });
                    break;
                }
                case 'lastMonth': {
                    const startDate = dayjs().subtract(1, 'month').startOf('month');
                    const endDate = dayjs().subtract(1, 'month').endOf('month');
                    setFilter({
                        ...filter,
                        dayFrom: startDate.format(),
                        dayTo: endDate.format(),
                        value: 'lastMonth',
                        ...resetTablePagination,
                    });
                    break;
                }
                case 'all': {
                    setFilter({ ...tableState, ...resetTablePagination });
                    break;
                }
                default:
                    break;
            }
            setDate(value);
        },
        [setFilter, filter, tableState, dispatch]
    );
    return (
        <Select
            disabled={disabled}
            value={date}
            onChange={(e) => handleChange(e.target.value)}
            sx={{
                width: 'auto',
                '& fieldset': {
                    border: 'none',
                },
            }}
        >
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="yesterday">Yesterday</MenuItem>
            <MenuItem value="thisMonth">This month</MenuItem>
            <MenuItem value="lastMonth">Last month</MenuItem>
            <MenuItem value="all">All</MenuItem>
        </Select>
    );
};
